import { BaseComponent2 } from '../../_common/client/base-component2';
import { push as showSnackbar } from '@otto-ec/otto-components/snackbar/v1';
import { trackEvent, trackView } from '@otto-ec/nexus-registry/pdp/tracking';
import { sheetV1 } from '@otto-ec/otto-components/sheet';

const REVIEWS_SHEET_HEADLINE = 'Kundenbewertungen zu diesem Punkt';

export class CustomerReviewSummaryComponent extends BaseComponent2 {
    public rootSelector = '.js_pdp_cr-summary';

    private readonly reviewsHtmlByUrl = new Map<string, Promise<string>>();
    private readonly sheetIdByReviewsUrl = new Map<string, string>();
    private sheetIdSequence = 0;

    public async init(): Promise<void> {
        this.performViewTracking();
        this.preFetchReviews();
        this.registerReviewLinks();
        this.initializeFeedback();
    }

    private performViewTracking(): void {
        trackView({
            product_ReviewSummary: ['True'],
        });
    }

    private getReviewLinks(): HTMLElement[] {
        return this.getChildElements<HTMLElement>('.js_pdp_cr-summary__reviews-link');
    }

    private preFetchReviews(): void {
        this.getReviewLinks().forEach(reviewLink => {
            const reviewsUrl = reviewLink.dataset.reviewsUrl;
            if (reviewsUrl) {
                this.getOrFetchReviewsHtml(reviewsUrl);
            }
        });
    }

    private getOrFetchReviewsHtml(reviewsUrl: string): Promise<string> {
        let reviewsHtmlPromise = this.reviewsHtmlByUrl.get(reviewsUrl);
        if (!reviewsHtmlPromise) {
            reviewsHtmlPromise = this.fetchReviewsHtml(reviewsUrl);
            this.reviewsHtmlByUrl.set(reviewsUrl, reviewsHtmlPromise);
        }
        return reviewsHtmlPromise;
    }

    private async fetchReviewsHtml(reviewsUrl: string): Promise<string> {
        const response = await fetch(reviewsUrl);
        return response.text();
    }

    private registerReviewLinks(): void {
        this.getReviewLinks().forEach(reviewLink => {
            reviewLink.addEventListener('click', event => {
                event.preventDefault();
                trackEvent({ product_ReviewSummary: ['ShowSingleReviews'] });
                this.openReviewsSheet(reviewLink);
            });
        });
    }

    private async openReviewsSheet(reviewLink: HTMLElement): Promise<void> {
        const reviewsUrl = reviewLink.dataset.reviewsUrl;
        if (!reviewsUrl) {
            return;
        }

        const sheetId = this.getOrCreateSheetId(reviewsUrl);
        const isNewSheet = !document.getElementById(sheetId);

        const sheet = await sheetV1.create(
            {
                id: sheetId,
                headline: REVIEWS_SHEET_HEADLINE,
                adaptiveAlignment: 'center',
            },
            { adjacentElement: reviewLink.closest<HTMLElement>('oc-sheet-v1') ?? reviewLink }
        );

        if (isNewSheet) {
            try {
                sheet.innerHTML = await this.getOrFetchReviewsHtml(reviewsUrl);
            } catch (error) {
                this.reviewsHtmlByUrl.delete(reviewsUrl);
                sheet.remove();
                this.logger?.logError({
                    message: 'Error loading reviews for review summary sheet',
                    error: error instanceof Error ? error : undefined,
                });
                showSnackbar('Die Bewertungen konnten leider nicht geladen werden.');
                return;
            }
        }

        sheet.open = true;
    }

    private getOrCreateSheetId(reviewsUrl: string): string {
        let sheetId = this.sheetIdByReviewsUrl.get(reviewsUrl);
        if (!sheetId) {
            sheetId = `pdp_cr-summary__reviews-sheet-${this.sheetIdSequence++}`;
            this.sheetIdByReviewsUrl.set(reviewsUrl, sheetId);
        }
        return sheetId;
    }

    private initializeFeedback(): void {
        this.getChildElements<HTMLOcButtonV1Element>('.js_pdp_cr-summary__feedback-button').forEach(buttonElement => {
            buttonElement.addEventListener('click', () => {
                const trackingValue = buttonElement.dataset.trackingValue;
                if (trackingValue) {
                    trackEvent({ product_ReviewSummary: [trackingValue] });
                }
                this.hideFeedback();
                showSnackbar('Vielen Dank für Dein Feedback!');
            });
        });
    }

    private hideFeedback(): void {
        this.getChildElement('.js_pdp_cr-summary__feedback')?.classList.add('pdp_cr-summary__feedback--hidden');
    }
}
